import React, { useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { DateTime } from 'luxon'
import { buildDateTime } from 'lib/CustomLuxonUtils'
import CustomLuxonAdapter from 'lib/CustomLuxonAdapter'
import DeleteOutlineIcon from 'components/material-symbol/icons/DeleteOutline'
import IconButton from '@material-ui/core/IconButton'
import { makeStyles } from '@material-ui/core/styles'
import Grid from '@material-ui/core/Grid'
import { toThreeLetterTimezone } from 'utils/timezones'
import PGATimePicker from '../pga-time-picker'
import isValid from 'date-fns/isValid'
import { LocalizationProvider, DesktopDatePicker } from '@mui/x-date-pickers'
import {
  mergeDesktopDatePickerSlotProps,
  mergeDesktopDatePickerSlots,
} from 'components/pga-time-picker/desktop-date-picker'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { calendarDatePickerTheme } from 'pages/pga-coach/bookings/schedule/calendar/calendar-date-picker-theme'
import EventIcon from 'components/material-symbol/icons/Event'
import Box from '@material-ui/core/Box'
import {
  outlinedPickerFieldSx,
  OUTLINED_INPUT_BORDER_RADIUS,
} from 'lib/form-field-styles'

const useStyles = makeStyles(theme => ({
  base: {
    background: 'rgb(255, 245, 229)',
    borderRadius: '10px',
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  ul: {
    marginTop: '4px',
    marginBottom: '0',
  },
  cardDivider: {
    margin: '0 8px',
    background: 'rgba(0, 35, 75, 0.24)',
  },
  logo: {
    color: '#ff9800',
    width: '50px',
    minWidth: '50px',
    alignSelf: 'center',
  },
  body: {
    padding: '8px 4px',
    justifyContent: 'start',
  },
  dateTimeRow: {
    marginBottom: '30px',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      alignItems: 'end',
    },
  },
  dateTimeRowError: {
    marginBottom: '30px',
    alignItems: 'flex-start',
    [theme.breakpoints.down('sm')]: {
      alignItems: 'baseline',
    },
  },
  dateInput: {
    order: 1,
  },
  timeInputs: {
    order: 2,
  },
  startTimeInput: {
    [theme.breakpoints.only('xs')]: {
      marginTop: theme.spacing(1.5),
    },
  },
  endTimeInput: {
    [theme.breakpoints.only('xs')]: {
      marginTop: theme.spacing(1.5),
    },
  },
  deleteSessionBtn: {
    [theme.breakpoints.only('xs')]: {
      marginTop: theme.spacing(1.5),
    },
  },
  divider: {
    margin: '30px 0px',
    borderTop: '1px solid #E0E0E0',
    borderBottom: '0',
  },
  additionalSessionInfo: {
    textAlign: 'center',
    paddingTop: '5px',
  },
  addSessionBtn: {
    marginTop: '15px',
  },
  multiSessionInfoText: {
    color: theme.palette.text.secondary,
    textAlign: 'center',
  },
  infoHeader: {
    paddingBottom: '5px',
  },
  sessionCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
  },
  fieldLabel: {
    fontSize: '0.6875rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.75),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    '& svg, & .MuiSvgIcon-root': {
      fontSize: 14,
    },
  },
  dateTimeRowEdit: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  dateTimeField: {
    flex: '1 1 0',
    minWidth: 0,
  },
  deleteSessionBtnEdit: {
    flex: '0 0 56px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}))

const SessionInput = ({
  session,
  timezone,
  onDelete,
  handleVerifySessions,
  index,
  name,
  fields,
  variant = 'default',
}) => {
  const classes = useStyles()
  const isEditVariant = variant === 'edit'
  const { control, register, errors, setValue } = useFormContext()

  const [startDate, setStartDate] = useState(session?.startDate)
  const [startTime, setStartTime] = useState(session?.startTime)
  const [endTime, setEndTime] = useState(session?.endTime)

  const defineTimezone = (session, timezone, field) => {
    const resultTimezone = toThreeLetterTimezone(
      timezone,
      buildDateTime(session.startDate, session[field]),
    )
    return resultTimezone ? `(${resultTimezone})` : ''
  }

  const startDateTime =
    startDate &&
    startTime &&
    buildDateTime(startDate, startTime, timezone).toUTC()

  const changeStartDate = value => {
    const date = new Date(value)

    if (isValid(date)) {
      setStartDate(date)
      handleVerifySessions()

      const day = date.getDate()
      const month = date.getMonth()

      const newStartTime = new Date(startTime)
      newStartTime.setDate(day)
      newStartTime.setMonth(month)
      setValue(`${name}[${index}].startTime`, DateTime.fromJSDate(newStartTime))

      const newEndTime = new Date(endTime)
      newEndTime.setDate(day)
      newEndTime.setMonth(month)
      setValue(`${name}[${index}].endTime`, DateTime.fromJSDate(newEndTime))
    }
  }

  const changeStartTime = value => {
    const date = new Date(value.ts)

    if (isValid(date)) {
      handleVerifySessions()

      const minutes = date.getMinutes()
      const roundedMinutes = Math.floor(minutes / 5) * 5
      date.setMinutes(roundedMinutes)
      setStartTime(date)
    }
  }

  const changeEndTime = value => {
    const date = new Date(value.ts)

    if (isValid(date)) {
      handleVerifySessions()

      const minutes = date.getMinutes()
      const roundedMinutes = Math.floor(minutes / 5) * 5
      date.setMinutes(roundedMinutes)

      setEndTime(date)
    }
  }
  const showDelete = fields.length > 1

  const startTimeLabel = `Start Time ${defineTimezone(
    session,
    timezone,
    'startTime',
  )}`.trim()
  const endTimeLabel = `End Time ${defineTimezone(
    session,
    timezone,
    'endTime',
  )}`.trim()

  const datePicker = (
    <LocalizationProvider
      dateAdapter={CustomLuxonAdapter}
      dateFormats={{ normalDateWithWeekday: 'LLL d, yyyy' }}
    >
      <ThemeProvider
        theme={createTheme(calendarDatePickerTheme, {
          shape: { borderRadius: OUTLINED_INPUT_BORDER_RADIUS },
        })}
      >
        <Controller
          control={control}
          name={`${name}[${index}].startDate`}
          defaultValue={session.startDate}
          render={({ onChange, value }) => (
            <DesktopDatePicker
              label="Date"
              id="start-date"
              name={`${name}[${index}].startDate`}
              innerRef={register}
              value={value}
              onChange={newValue => {
                onChange(newValue)
                changeStartDate(newValue)
              }}
              disablePast
              format={isEditVariant ? 'LLL d, yyyy' : 'MMM dd, yyyy'}
              dayOfWeekFormatter={day => `${day}`}
              closeOnSelect
              sx={{ width: '100%' }}
              slots={mergeDesktopDatePickerSlots({
                openPickerIcon: EventIcon,
              })}
              slotProps={mergeDesktopDatePickerSlotProps({
                textField: {
                  ...(isEditVariant
                    ? { sx: outlinedPickerFieldSx, variant: 'outlined' }
                    : {}),
                  error: !!errors[name]?.[index],
                  helperText:
                    errors[name]?.[index]?.startDate?.message ||
                    errors[name]?.[index]?.message,
                },
              })}
            />
          )}
        />
      </ThemeProvider>
    </LocalizationProvider>
  )

  const editPickerSlotProps = isEditVariant
    ? {
      textField: {
        sx: outlinedPickerFieldSx,
        variant: 'outlined',
      },
    }
    : undefined

  const startTimePicker = (
    <PGATimePicker
      id="start-time"
      name={`${name}[${index}].startTime`}
      defaultValue={session.startTime}
      date={startDateTime}
      error={!!errors[name]?.[index]}
      helperText={errors[name]?.[index]?.startTime?.message}
      onChanged={newValue => {
        changeStartTime(newValue)
      }}
      label={
        isEditVariant
          ? startTimeLabel
          : `Start time ${defineTimezone(session, timezone, 'startTime')}`
      }
      minutesStep={5}
      format="h:mm a"
      slotProps={editPickerSlotProps}
    />
  )

  const endTimePicker = (
    <PGATimePicker
      id="end-time"
      name={`${name}[${index}].endTime`}
      defaultValue={session.endTime}
      date={startDateTime}
      error={!!errors[name]?.[index]}
      helperText={errors[name]?.[index]?.endTime?.message}
      onChanged={newValue => changeEndTime(newValue)}
      label={
        isEditVariant
          ? endTimeLabel
          : `End time ${defineTimezone(session, timezone, 'endTime')}`
      }
      horizontalAnchor="right"
      minutesStep={5}
      format="h:mm a"
      slotProps={editPickerSlotProps}
    />
  )

  if (isEditVariant) {
    return (
      <Box className={classes.dateTimeRowEdit}>
        <Box className={classes.dateTimeField}>{datePicker}</Box>
        <Box className={classes.dateTimeField}>{startTimePicker}</Box>
        <Box className={classes.dateTimeField}>{endTimePicker}</Box>
        {showDelete ? (
          <Box className={classes.deleteSessionBtnEdit}>
            <IconButton
              onClick={() => onDelete(index)}
              aria-label="Delete session"
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Box>
        ) : null}
      </Box>
    )
  }

  const content = (
    <Grid
      className={
        errors?.sessions?.[index]
          ? classes.dateTimeRowError
          : classes.dateTimeRow
      }
      container
      spacing={1}
    >
      <Grid
        item
        xs={12}
        sm={fields.length > 1 ? 5 : 6}
        className={classes.dateInput}
      >
        {datePicker}
      </Grid>
      <Grid
        item
        xs={12}
        sm={fields.length > 1 ? 7 : 6}
        className={classes.timeInputs}
      >
        <Grid container spacing={1}>
          <Grid
            item
            xs={fields.length > 1 ? 5 : 6}
            className={classes.startTimeInput}
          >
            {startTimePicker}
          </Grid>
          <Grid
            item
            xs={fields.length > 1 ? 5 : 6}
            className={classes.endTimeInput}
          >
            {endTimePicker}
          </Grid>
          {showDelete && (
            <Grid item xs={2} className={classes.deleteSessionBtn}>
              <IconButton onClick={() => onDelete(index)}>
                <DeleteOutlineIcon color="primary" />
              </IconButton>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  )

  return content
}

export default SessionInput
